import React, { useEffect, useRef, useState } from "react";

import { fetchCountries, fetchCitiesByCountry } from "../../../../services/api";
import type { SearchableDropdownProps } from "../../../../types/types";
import type { TranslationFn } from "../dealFormFields";

const _countriesCache: {
  data: string[] | null;
  promise: Promise<string[]> | null;
} = {
  data: null,
  promise: null,
};

export function prefetchCountries(): void {
  if (_countriesCache.data || _countriesCache.promise) return;
  _countriesCache.promise = fetchCountries()
    .then((d) => {
      _countriesCache.data = d;
      return d;
    })
    .catch(() => []);
}

const GlobeIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400 flex-shrink-0 mr-2"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="10" />
    <path
      strokeLinecap="round"
      d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20"
    />
  </svg>
);

const CityIcon = () => (
  <svg
    className="w-4 h-4 text-slate-400 flex-shrink-0 mr-2"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 21h18M3 7l9-4 9 4M4 21V7m16 14V7M9 21V11h6v10"
    />
  </svg>
);

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  options,
  placeholder,
  disabled,
  loading,
  icon,
  onChange,
  isInvalid,
  tr,
}) => {
  const [input, setInput] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(value ?? "");
  }, [value]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = input.trim()
    ? options.filter((o) => o.toLowerCase().includes(input.toLowerCase()))
    : options;

  const borderClass = disabled
    ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
    : open
      ? "border-blue-500 ring-2 ring-blue-100"
      : isInvalid
        ? "border-red-400 bg-red-50"
        : "border-slate-200 hover:border-slate-400";

  return (
    <div ref={ref} className="relative w-full">
      <div
        onClick={() => {
          if (!disabled && !loading) setOpen((o) => !o);
        }}
        className={`flex items-center h-10 w-full rounded-lg border bg-white px-3 cursor-pointer transition-all duration-150 ${borderClass}`}
      >
        {icon === "globe" ? <GlobeIcon /> : <CityIcon />}

        <input
          type="text"
          value={loading ? "" : input}
          placeholder={loading ? tr("loading") : placeholder}
          disabled={disabled || loading}
          onChange={(e) => {
            setInput(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => {
            if (!disabled && !loading) setOpen(true);
          }}
          className="flex-1 text-[14px] text-slate-800 bg-transparent outline-none placeholder:text-slate-400 min-w-0 disabled:cursor-not-allowed cursor-text"
        />

        {loading && (
          <svg
            className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
            />
          </svg>
        )}

        {!loading && input && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setInput("");
              onChange("");
              setOpen(true);
            }}
            className="text-slate-300 hover:text-slate-500 transition flex-shrink-0 mr-1"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {!loading && (
          <svg
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </div>

      {open && !disabled && !loading && (
        <ul className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <li
                key={opt}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setInput(opt);
                  onChange(opt);
                  setOpen(false);
                }}
                className={`flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC] ${opt === input ? "bg-[#EBEBEC] text-gray-900 font-semibold" : "text-gray-900"}`}
              >
                {opt === input && (
                  <svg
                    className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m4.5 12.75 6 6 9-13.5"
                    />
                  </svg>
                )}
                <span className={opt === input ? "" : "ml-5"}>{opt}</span>
              </li>
            ))
          ) : (
            <li className="px-3 py-2.5 text-[13px] text-slate-400 text-center">
              {tr("noResultsFound")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export const CountryDropdown: React.FC<{
  value: string;
  onChange: (val: string) => void;
  isInvalid?: boolean;
  tr: TranslationFn;
}> = ({ value, onChange, isInvalid, tr }) => {
  const [countries, setCountries] = useState<string[]>(
    _countriesCache.data ?? [],
  );
  const [loading, setLoading] = useState(!_countriesCache.data);

  useEffect(() => {
    if (_countriesCache.data) {
      setCountries(_countriesCache.data);
      setLoading(false);
      return;
    }
    if (!_countriesCache.promise) prefetchCountries();
    _countriesCache.promise!.then((data) => {
      setCountries(data);
      setLoading(false);
    });
  }, []);

  return (
    <SearchableDropdown
      value={value}
      options={countries}
      placeholder={tr("searchCountry")}
      icon="globe"
      loading={loading}
      onChange={onChange}
      isInvalid={isInvalid}
      tr={tr}
    />
  );
};

export const CityDropdown: React.FC<{
  value: string;
  country: string;
  onChange: (val: string) => void;
  isInvalid?: boolean;
  tr: TranslationFn;
}> = ({ value, country, onChange, isInvalid, tr }) => {
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const prevCountry = useRef("");
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!country) {
      setCities([]);
      prevCountry.current = "";
      return;
    }
    if (country === prevCountry.current && cities.length > 0) return;

    if (prevCountry.current && prevCountry.current !== country) {
      onChangeRef.current("");
    }

    prevCountry.current = country;
    setLoading(true);
    setCities([]);

    fetchCitiesByCountry(country)
      .then(setCities)
      .catch((e) => console.error("Failed to load cities", e))
      .finally(() => setLoading(false));
  }, [country]);

  return (
    <SearchableDropdown
      value={value}
      options={cities}
      placeholder={!country ? tr("selectCountryFirst") : tr("searchCity")}
      icon="city"
      disabled={!country}
      loading={loading}
      onChange={onChange}
      isInvalid={isInvalid}
      tr={tr}
    />
  );
};
